import { EventEmitter, Injectable, inject } from '@angular/core';
import { AccountService } from '@nano/account/common';
import { RoutingService } from '@nano/common/routing';
import { AuthService, UserActivityStatus } from '@nano/core/auth';
import { CONFIG } from '@nano/core/config';
import { PaymentModalService } from '@nano/payment/payment-modal';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { NavService } from '../nav.service';

@Injectable({ providedIn: 'root' })
export class PaymentMenuItemsService {
  readonly makePaymentEnabled$: Observable<boolean>;

  readonly importPaymentsEnabled$: Observable<boolean>;
  readonly importPaymentsVisible$: Observable<boolean>;

  readonly scheduledPaymentsEnabled$: Observable<boolean>;
  readonly scheduledPaymentsVisible$: Observable<boolean>;

  readonly paymentBeneficiariesVisible$: Observable<boolean>;

  readonly userHasPartyAndRealAccountAndNotFailed$: Observable<boolean>;

  readonly pendingPaymentsEnabled$: Observable<boolean>;

  readonly actionPerformed$: EventEmitter<void> = new EventEmitter<void>();

  private config = inject(CONFIG);

  constructor(
    private accountService: AccountService,
    private authService: AuthService,
    private paymentModalService: PaymentModalService,
    private routingService: RoutingService,
    private navService: NavService
  ) {
    const user$ = this.authService.userProfile$.pipe(filter((userProfile) => !!userProfile));
    this.userHasPartyAndRealAccountAndNotFailed$ = this.navService.userHasPartyAndRealAccountAndNotFailed$;
    this.pendingPaymentsEnabled$ = this.navService.pendingPaymentsVisible$;
    this.makePaymentEnabled$ = combineLatest([this.accountService.outgoingPaymentsAvailable$, this.userHasPartyAndRealAccountAndNotFailed$, user$]).pipe(
      map(
        ([outgoingPaymentsAvailable, userHasPartyAndRealAccountAndNotFailed, user]) =>
          outgoingPaymentsAvailable &&
          userHasPartyAndRealAccountAndNotFailed &&
          !(user.activityStatus === UserActivityStatus.SUSPENDED || user.ibUserParty.activityStatus === UserActivityStatus.SUSPENDED)
      )
    );

    this.importPaymentsEnabled$ = this.userHasPartyAndRealAccountAndNotFailed$;
    this.importPaymentsVisible$ = user$.pipe(
      map((user) => (this.config.paymentImportPrivateEnabled && user.privatePerson) || (this.config.paymentImportCorporateEnabled && !user.privatePerson))
    );

    this.scheduledPaymentsEnabled$ = this.userHasPartyAndRealAccountAndNotFailed$;
    this.scheduledPaymentsVisible$ = of(this.config.scheduledPaymentsEnabled);

    this.paymentBeneficiariesVisible$ = of(this.config.paymentBeneficiaryManagementEnabled);
  }

  makePayment(): Observable<unknown> {
    return this.makePaymentEnabled$.pipe(
      tap((enabled) => {
        if (enabled) {
          this.actionPerformed$.next();
          this.paymentModalService.openModal();
        }
      })
    );
  }

  importPayments(): Observable<unknown> {
    return this.importPaymentsEnabled$.pipe(
      tap((enabled) => {
        if (enabled) {
          this.actionPerformed$.next();
          this.routingService.navigateToPaymentImport();
        }
      })
    );
  }

  pendingPayments(): Observable<unknown> {
    return this.pendingPaymentsEnabled$.pipe(
      tap((enabled) => {
        if (enabled) {
          this.actionPerformed$.next();
          this.routingService.navigateToPendingPayments();
        }
      })
    );
  }

  scheduledPayments(): Observable<unknown> {
    return this.scheduledPaymentsEnabled$.pipe(
      tap((enabled) => {
        if (enabled) {
          this.actionPerformed$.next();
          this.routingService.navigateToScheduledPayments();
        }
      })
    );
  }

  paymentBeneficiaries(): Observable<unknown> {
    return of({}).pipe(
      tap(() => {
        this.actionPerformed$.next();
        this.routingService.navigateToPaymentBeneficiaries();
      })
    );
  }
}
